import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useMemo,
} from "react";
import {
    functionalUpdate,
    type OnChangeFn,
    type RowSelectionState,
} from "@tanstack/react-table";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type { BoardLayout } from "@components/views/board/boardLayoutCache";
import { getWorkRows } from "./board/workBoardColumns";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkspaceRow,
} from "./clientsWorkOverviewViewConfig";
import { getPersonCopySourceRowId } from "./workPersonGroups";

interface UseWorkRowSelectionArgs {
    /** List's rows; person groupings hold a copy of an engagement per person. */
    tableData: ClientsWorkOverviewRow[] | null;
    /** The page's engagements, whose row ids Board's cards select by. */
    workspaceRows: ClientsWorkOverviewWorkspaceRow[] | null;
    /** The shown layout, whose rows the selection keeps. */
    layout: BoardLayout;
    rowSelection: RowSelectionState;
    setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
}

const withoutRowIds = (
    rowSelection: RowSelectionState,
    rowIds: readonly string[],
): RowSelectionState => {
    const removedIds = new Set(rowIds);
    return Object.fromEntries(
        Object.entries(rowSelection).filter(([id]) => !removedIds.has(id)),
    );
};

/** A shown work row, except busy closes demo work, which can't be selected. */
const isSelectableListRow = ({
    closeIdsByRowId,
    rowId,
}: {
    closeIdsByRowId: ReadonlyMap<string, string>;
    rowId: string;
}) => {
    const closeId = closeIdsByRowId.get(rowId);
    return closeId !== undefined && !isBusyClosesDemoId(closeId);
};

/** A selected engagement the layout shows: its close id and the selected row ids for it. */
export interface SelectedWorkEngagement {
    closeId: string;
    rowIds: string[];
}

/**
 * The Work page's row selection, keyed by row id in both layouts. In List an
 * engagement under two person groups is one selection: checking either copy
 * checks both, and it counts and deletes once. Selected rows the layout
 * doesn't show (another page, filters, view or List grouping changes) are
 * held: they stop counting and can't be deleted until they show again.
 */
export const useWorkRowSelection = ({
    tableData,
    workspaceRows,
    layout,
    rowSelection,
    setRowSelection,
}: UseWorkRowSelectionArgs) => {
    const shownRows = layout === "board" ? workspaceRows : tableData;

    const listRowIdsByCloseId = useMemo(() => {
        const rowIdsByCloseId = new Map<string, string[]>();
        for (const { id, closeId } of getWorkRows(tableData ?? [])) {
            rowIdsByCloseId.set(closeId, [
                ...(rowIdsByCloseId.get(closeId) ?? []),
                id,
            ]);
        }
        return rowIdsByCloseId;
    }, [tableData]);

    const closeIdsByRowId = useMemo(
        () =>
            new Map(
                getWorkRows(shownRows ?? []).map(({ id, closeId }) => [
                    id,
                    closeId,
                ]),
            ),
        [shownRows],
    );

    const visibleRowSelection = useMemo((): RowSelectionState => {
        const isAllVisible = Object.keys(rowSelection).every(
            (id) => !rowSelection[id] || closeIdsByRowId.has(id),
        );
        if (isAllVisible) {
            return rowSelection;
        }
        return Object.fromEntries(
            Object.entries(rowSelection).filter(
                ([id, isSelected]) => isSelected && closeIdsByRowId.has(id),
            ),
        );
    }, [rowSelection, closeIdsByRowId]);

    const onListRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
        (updater) =>
            setRowSelection((previous) => {
                const next = { ...functionalUpdate(updater, previous) };
                const toggledRowIds = Object.keys({
                    ...previous,
                    ...next,
                }).filter((rowId) => !previous[rowId] !== !next[rowId]);
                for (const rowId of toggledRowIds) {
                    if (!next[rowId]) {
                        const sourceRowId = getPersonCopySourceRowId(rowId);
                        for (const storedId of Object.keys(next)) {
                            if (
                                getPersonCopySourceRowId(storedId) ===
                                sourceRowId
                            ) {
                                delete next[storedId];
                            }
                        }
                        continue;
                    }
                    const closeId = closeIdsByRowId.get(rowId);
                    const copyIds =
                        closeId === undefined
                            ? []
                            : (listRowIdsByCloseId.get(closeId) ?? []);
                    for (const copyId of copyIds) {
                        next[copyId] = true;
                    }
                }
                return next;
            }),
        [setRowSelection, closeIdsByRowId, listRowIdsByCloseId],
    );

    const toggleListRow = useCallback(
        (rowId: string) => {
            if (!isSelectableListRow({ closeIdsByRowId, rowId })) {
                return;
            }
            onListRowSelectionChange((previous) =>
                previous[rowId]
                    ? withoutRowIds(previous, [rowId])
                    : { ...previous, [rowId]: true },
            );
        },
        [closeIdsByRowId, onListRowSelectionChange],
    );

    const selectListRow = useCallback(
        (rowId: string) => {
            if (!isSelectableListRow({ closeIdsByRowId, rowId })) {
                return;
            }
            onListRowSelectionChange((previous) =>
                previous[rowId] ? previous : { ...previous, [rowId]: true },
            );
        },
        [closeIdsByRowId, onListRowSelectionChange],
    );

    const toggleAllListRows = useCallback(() => {
        const workRowIds = [...listRowIdsByCloseId]
            .filter(([closeId]) => !isBusyClosesDemoId(closeId))
            .flatMap(([, rowIds]) => rowIds);
        onListRowSelectionChange((previous) =>
            workRowIds.every((rowId) => previous[rowId])
                ? withoutRowIds(previous, workRowIds)
                : {
                      ...previous,
                      ...Object.fromEntries(
                          workRowIds.map((rowId) => [rowId, true]),
                      ),
                  },
        );
    }, [listRowIdsByCloseId, onListRowSelectionChange]);

    const selectedEngagements = useMemo((): SelectedWorkEngagement[] => {
        const engagementsByCloseId = new Map<string, SelectedWorkEngagement>();
        for (const rowId of Object.keys(visibleRowSelection)) {
            const closeId = closeIdsByRowId.get(rowId);
            if (!visibleRowSelection[rowId] || closeId === undefined) {
                continue;
            }
            const engagement = engagementsByCloseId.get(closeId) ?? {
                closeId,
                rowIds: [],
            };
            engagement.rowIds.push(rowId);
            engagementsByCloseId.set(closeId, engagement);
        }
        return [...engagementsByCloseId.values()];
    }, [visibleRowSelection, closeIdsByRowId]);

    return {
        /** The selection limited to rows the layout shows; other picks stay held and return with their rows. */
        visibleRowSelection,
        onListRowSelectionChange,
        /** x in List: toggles a work row with its copies; other rows and busy closes demo work are ignored. */
        toggleListRow,
        /** Shift+Arrow from outside List's grid: selects a work row with its copies; other rows and busy closes demo work are ignored. */
        selectListRow,
        /** Shift+A in List: selects every work row but busy closes demo work, or clears them once all are selected. */
        toggleAllListRows,
        selectedEngagements,
        /** Engagements in List, each once however many groups show it. */
        applicationCount: listRowIdsByCloseId.size,
    };
};
